import React from "react"
import { Trash2, Info } from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: React.ReactNode
  confirmText?: string
  cancelText?: string
  onConfirm: () => void
  variant?: "default" | "destructive"
  isConfirming?: boolean
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  variant = "default",
  isConfirming = false,
}: ConfirmDialogProps) {
  const isDestructive = variant === "destructive"

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="p-0 overflow-hidden sm:rounded-[24px] max-w-sm border-0 shadow-2xl bg-white dark:bg-slate-900">
        <div className="p-8 pb-6 flex flex-col items-center text-center">
          {/* Animated Icon Container */}
          <div className="relative mb-6">
            <div className={`absolute inset-0 rounded-full blur-xl opacity-40 ${isDestructive ? 'bg-red-500' : 'bg-blue-500'}`} />
            <div
              className={`relative w-16 h-16 rounded-full flex items-center justify-center shadow-inner ${
                isDestructive
                  ? "bg-gradient-to-b from-red-50 to-red-100/50 dark:from-red-900/40 dark:to-red-900/10 border border-red-200/50 dark:border-red-800/50 text-red-600"
                  : "bg-gradient-to-b from-blue-50 to-blue-100/50 dark:from-blue-900/40 dark:to-blue-900/10 border border-blue-200/50 dark:border-blue-800/50 text-blue-600"
              }`}
            >
              {isDestructive ? <Trash2 className="w-7 h-7 stroke-[2.5px]" /> : <Info className="w-7 h-7 stroke-[2.5px]" />}
            </div>
          </div>
          
          <AlertDialogHeader className="space-y-3">
            <AlertDialogTitle className="text-2xl font-black text-slate-900 dark:text-white tracking-tight text-center">
              {title}
            </AlertDialogTitle>
            {description && (
              <AlertDialogDescription className="text-[15px] text-slate-500 dark:text-slate-400 leading-relaxed text-center">
                {description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
        </div>
        
        {/* Actions Area */}
        <div className="px-8 pb-8 pt-2 flex flex-col gap-3">
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              onConfirm()
            }}
            disabled={isConfirming}
            className={`w-full h-12 rounded-[14px] font-bold shadow-lg shadow-black/5 transition-transform active:scale-[0.98] ${
              isDestructive
                ? "bg-red-600 hover:bg-red-700 text-white border-0 ring-4 ring-red-600/20"
                : "bg-primary hover:bg-primary/90 text-white border-0 ring-4 ring-primary/20"
            }`}
          >
            {isConfirming ? (
              <div className="flex items-center justify-center gap-2">
                <span className="w-5 h-5 rounded-full border-2 border-white/20 border-t-white animate-spin" />
                <span>Processing...</span>
              </div>
            ) : (
              confirmText
            )}
          </AlertDialogAction>
          
          <AlertDialogCancel
            disabled={isConfirming}
            className="w-full h-12 rounded-[14px] font-bold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border-0 mt-0 transition-colors"
          >
            {cancelText}
          </AlertDialogCancel>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
